import { buildAssistantMessages, parseA2uiString } from './utils';

import type {
  DeepSearchConversation,
  DeepSearchMessage,
  DeepSearchMessageAttachment,
} from '../types';
import type { A2uiMessage } from '@a2ui/web_core/v0_9';
import type { DeepsearchSession } from '@ltvco/refresh-lib/hooks';

interface InlineDataShape {
  mimeType: string;
  data: string;
}

// Persisted session events include inline_data parts for uploaded files, but
// `DeepsearchContentPart` in refresh-lib doesn't expose that field (only text,
// functionCall, functionResponse). Cast the same way the a2ui extraction does
// above.
function extractInlineData(part: unknown): InlineDataShape | undefined {
  const inlineData = (part as { inlineData?: InlineDataShape }).inlineData;
  if (!inlineData || !inlineData.mimeType || !inlineData.data) {
    return undefined;
  }
  return inlineData;
}

function base64ToObjectUrl(base64: string, mimeType: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

function inlineDataToAttachment(
  inlineData: InlineDataShape
): DeepSearchMessageAttachment | undefined {
  const { mimeType, data } = inlineData;
  if (mimeType.startsWith('image/')) {
    // Convert once to an object URL so the raw base64 string can be GC'd
    // instead of persisting in state for the whole session.
    return {
      kind: 'image',
      mimeType,
      previewUrl: base64ToObjectUrl(data, mimeType),
    };
  }
  if (mimeType === 'application/pdf') {
    return { kind: 'pdf', mimeType, previewUrl: '' };
  }
  if (mimeType === 'text/csv' || mimeType === 'application/csv') {
    return { kind: 'csv', mimeType, previewUrl: '' };
  }
  return undefined;
}

// Teaser card clicks append `. {idType}: {idValue}` to the API message but
// only show the display text to the user. Strip these suffixes so history
// matches what was shown at send time.
// Match only teaser-style opaque IDs at the end of the message.
// Prevents stripping normal prose like "... . id: my account id".
const TEASER_ID_SUFFIX =
  /\. (?:bv_id|source_id|fs_id|property_id|id): [A-Za-z0-9_-]{8,}$/;

function stripTeaserIdSuffix(text: string): string {
  return text.replace(TEASER_ID_SUFFIX, '');
}

export function convertSessionToConversation(
  session: DeepsearchSession
): DeepSearchConversation {
  const messages: DeepSearchMessage[] = [];
  let pendingA2ui: A2uiMessage[] = [];

  for (const event of session.events ?? []) {
    // Collect a2ui emitted by render tools before the agent's text response.
    for (const part of event.content?.parts ?? []) {
      const a2uiStr = (
        part as { functionResponse?: { response?: { a2ui?: unknown } } }
      ).functionResponse?.response?.a2ui;
      if (typeof a2uiStr === 'string') {
        const parsed = parseA2uiString(a2uiStr);
        if (parsed.length > 0) {
          pendingA2ui = parsed;
        }
      }
    }

    const text = event.content?.parts
      ?.filter((p) => p.text)
      .map((p) => p.text ?? '')
      .join('');

    if (event.author === 'user') {
      // Reconstruct the first supported inline_data part into an attachment so
      // uploaded files still render when reloading an old conversation. The
      // original filename isn't persisted, so `fileName` is intentionally left
      // undefined and FilePreview will render only the thumbnail/icon.
      let attachment: DeepSearchMessageAttachment | undefined;
      for (const part of event.content?.parts ?? []) {
        const inlineData = extractInlineData(part);
        if (!inlineData) {
          continue;
        }
        const built = inlineDataToAttachment(inlineData);
        if (built) {
          attachment = built;
          break;
        }
      }

      if (!text && !attachment) {
        continue;
      }

      messages.push({
        id: event.id,
        role: 'user',
        data: text ? stripTeaserIdSuffix(text) : '',
        attachment,
      });
    } else {
      if (!text) {
        continue;
      }
      const a2ui = pendingA2ui.length > 0 ? pendingA2ui : undefined;
      pendingA2ui = [];
      messages.push(...buildAssistantMessages(text, a2ui));
    }
  }

  const agentOutput =
    typeof session.state['volt:agent_output'] === 'string'
      ? session.state['volt:agent_output']
      : null;

  if (messages.length === 0 && agentOutput) {
    messages.push(...buildAssistantMessages(agentOutput));
  }

  const normalizedSessionTitle =
    typeof session.state['volt:session_title'] === 'string'
      ? session.state['volt:session_title'].trim()
      : '';
  const sessionTitle =
    normalizedSessionTitle.length > 0 ? normalizedSessionTitle : null;

  const rawTitle = sessionTitle
    ? sessionTitle
    : agentOutput
    ? agentOutput.split('\n')[0].replace(/\*+/g, '').trim()
    : '';
  // Store the full title; the sidebar truncates responsively with a CSS
  // ellipsis (+ hover tooltip), so titles fill the available width at every
  // breakpoint instead of being hard-capped to a fixed character count.
  const title = rawTitle.length === 0 ? 'New conversation' : rawTitle;

  return {
    id: session.id,
    title,
    lastUpdateTime: session.lastUpdateTime,
    messages,
  };
}
